// --- Core ---
import {ReactElement, useContext, useDebugValue, useMemo} from "react";
import {any, flatten, map} from "underscore";
// --- SaniQ ---
import {PermissionContext} from "../config/permissions";
import {ModuleDefinition, PermissionAccessLevel, PermissionKey} from "vendor/qurasoft/definitions/types/permissions";
import {TenantModuleContext} from "../config/tenantModules";
import definitions from "vendor/qurasoft/definitions/permissions.json";
import {ModuleKey} from "vendor/qurasoft/definitions/types/shared/moduleKey";
import {findTenantModule} from "helpers/TenantModules";

/**
 * Check if a permission allows the access level. Should generally not be used
 */
const isPermitted = (permission: PermissionAccessLevel, access: PermissionAccessLevel): boolean => {
  switch (access) {
    case 'read': {
      return permission === 'read' || permission === 'write';
    }
    case 'write':
      return permission === 'write';
    default:
      return false;
  }
};

/**
 * Check if the user is allowed to access the specified permission
 *
 * @param key The permission key
 * @param access The required access level
 * @param fallback The fallback return value if the permission cannot be found
 */
const usePermission = (key: PermissionKey, access: PermissionAccessLevel = 'read', fallback: boolean = false): boolean => {
  const permissions = useContext(PermissionContext);
  const result: boolean = key in permissions ? isPermitted(permissions[key], access) : fallback;
  useDebugValue(`${key}: ${access} = ${result}`);
  return result;
};

/**
 * Check if the user has read access for any of the permissions
 *
 * @param keys The list of permission keys that will be checked for read access
 */
const useAnyReadPermissions = (keys: PermissionKey[]): boolean => {
  const permissions = useContext(PermissionContext);

  if (!keys || keys.length === 0) {
    return true;
  }

  return any(keys, (key) => isPermitted(permissions[key], "read"));
};

/**
 * Check which permissions are not available due to tenant configuration.
 *
 * Is used to hide these permissions in the role editor;
 */
const useHiddenPermissions = (): PermissionKey[] => {
  const tenantModules = useContext(TenantModuleContext);

  return useMemo(() => (
    flatten(
      map(definitions.modules,
        (module: ModuleDefinition, key: ModuleKey) => findTenantModule(tenantModules, key, true)?.isEnabled ? [] : module.permissions
      )
    )
  ), [tenantModules]);
};

interface PermissionConditionProps {
  /**
   * When the permission is granted, show the children
   */
  children: ReactElement | ReactElement[];
  /**
   * When the permission is not granted show the optional fallback instead
   */
  fallback?: ReactElement;
  permission: PermissionKey;
  is?: PermissionAccessLevel;
}

/**
 * Render part of the component tree only, when the currently logged-in user is granted the required permission
 *
 * By default, only read access is necessary, but it can be further restricted to write access
 */
const PermissionCondition = ({permission, fallback = null, is = 'read', children}: PermissionConditionProps) => {
  const enabled = usePermission(permission, is);

  if (enabled) {
    return children;
  }

  return fallback;
};

export {
  isPermitted,
  useAnyReadPermissions,
  useHiddenPermissions,
  usePermission,
  PermissionCondition
};
